/**
 * Gauntlet Debates - A platform for structured, AI-moderated honorable debates
 * Copyright (c) 2025 Gauntlet Debates. All rights reserved.
 * 
 * This file is part of the Gauntlet Debates platform and is protected by 
 * copyright and other intellectual property laws. Unauthorized reproduction,
 * distribution, or disclosure of this file or its contents is prohibited.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Link as RouterLink } from 'react-router-dom';
import logoImage from '../assets/images/gd-logo-plain-white.svg';
import {
  Box,
  Button,
  Container,
  Typography,
  IconButton,
  useTheme,
  useMediaQuery,
  Grid,
  Card,
  CardActionArea,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { 
  Info as InfoIcon,
  EmojiEvents as TrophyIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import EmailVerificationGuard from '../components/EmailVerificationGuard';
import LanguageSwitcher from '../components/LanguageSwitcher';
import { useTranslation } from 'react-i18next';
import logger from '../utils/logger';
import { 
  Login as LoginIcon,
  Person as PersonIcon,
  AccountCircle as AccountIcon,
} from '@mui/icons-material';

// Custom icon component for CUBE
const CubeIcon = () => (
  <svg 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="currentColor"
    style={{ display: 'inline-block' }}
  >
    <path d="M21,16.5C21,16.88 20.79,17.21 20.47,17.38L12.57,21.82C12.41,21.94 12.21,22 12,22C11.79,22 11.59,21.94 11.43,21.82L3.53,17.38C3.21,17.21 3,16.88 3,16.5V7.5C3,7.12 3.21,6.79 3.53,6.62L11.43,2.18C11.59,2.06 11.79,2 12,2C12.21,2 12.41,2.06 12.57,2.18L20.47,6.62C20.79,6.79 21,7.12 21,7.5V16.5M12,4.15L6.04,7.5L12,10.85L17.96,7.5L12,4.15M5,15.91L11,19.29V12.58L5,9.21V15.91M19,15.91V9.21L13,12.58V19.29L19,15.91Z" />
  </svg>
);

const LandingPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t, i18n } = useTranslation();
  const [authMenuAnchorEl, setAuthMenuAnchorEl] = React.useState<null | HTMLElement>(null);

  // Add a staging environment indicator 
  const isStagingEnvironment = window.location.hostname.includes('staging');

  // Debug: Check what's actually in the i18next store
  React.useEffect(() => {
    logger.info('🔍 i18next Debug - Current language:', i18n.language);
    logger.info('🔍 i18next Debug - Available languages:', i18n.languages);
    
    // Check if Spanish data exists
    const esData = (i18n.store?.data as any)?.es?.common;
    logger.info('🔍 i18next Debug - Spanish common data exists:', !!esData);
    
    if (esData) {
      logger.info('🔍 i18next Debug - Spanish login value:', (esData as any).login);
      logger.info('🔍 i18next Debug - Spanish landingPage exists:', !!(esData as any).landingPage);
      if ((esData as any).landingPage) {
        logger.info('🔍 i18next Debug - Spanish landingPage.title:', (esData as any).landingPage.title);
      }
    }
    
    // Test direct translation calls
    logger.info('🔍 i18next Debug - Direct t("login"):', t('login'));
    logger.info('🔍 i18next Debug - Direct t("landingPage.title"):', t('landingPage.title'));
  }, [i18n.language, t, i18n, location]);

  const handleAuthMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAuthMenuAnchorEl(event.currentTarget);
  };

  const handleAuthMenuClose = () => {
    setAuthMenuAnchorEl(null);
  };

  const handleLogin = () => {
    navigate('/login');
    handleAuthMenuClose();
  };

  const handleRegister = () => {
    navigate('/register');
    handleAuthMenuClose();
  };

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column',
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 100%)',
      position: 'relative',
      pb: { xs: 5, sm: 4 }
    }}>
      {/* Staging Environment Banner */}
      {isStagingEnvironment && (
        <div style={{
          backgroundColor: '#ff9800',
          color: 'white',
          padding: '8px',
          textAlign: 'center',
          fontWeight: 'bold',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 9999
        }}>
          🚧 STAGING ENVIRONMENT - Test Mode 🚧
        </div>
      )}

      {/* Top Right Icons: Language, Auth, Info */}
      <Box sx={{ 
        position: 'absolute', 
        top: 16, 
        right: 16,
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        {/* Language Switcher with white styling */}
        <Box sx={{ 
          '& .MuiIconButton-root': { 
            color: 'rgba(255, 255, 255, 0.6) !important',
            '&:hover': { 
              color: 'rgba(255, 255, 255, 0.9) !important',
              backgroundColor: 'rgba(255, 255, 255, 0.1)' 
            }
          }
        }}>
          <LanguageSwitcher variant="icon" />
        </Box>
        
        {/* Auth dropdown - only show if not authenticated */}
        {!isAuthenticated && (
          <>
            <IconButton
              onClick={handleAuthMenuOpen}
              aria-label="Account"
              sx={{ 
                color: 'rgba(255, 255, 255, 0.6)',
                '&:hover': { 
                  color: 'rgba(255, 255, 255, 0.9)',
                  backgroundColor: 'rgba(255, 255, 255, 0.1)' 
                }
              }}
            >
              <AccountIcon />
            </IconButton>
            <Menu
              anchorEl={authMenuAnchorEl}
              open={Boolean(authMenuAnchorEl)}
              onClose={handleAuthMenuClose}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
              sx={{
                '& .MuiPaper-root': {
                  backgroundColor: 'rgba(26, 26, 46, 0.95)',
                  backdropFilter: 'blur(10px)',
                  border: '1px solid rgba(212, 175, 55, 0.3)',
                  color: 'white'
                }
              }}
            >
              <MenuItem onClick={handleLogin}>
                <ListItemIcon>
                  <LoginIcon sx={{ color: 'rgba(255, 255, 255, 0.7)' }} />
                </ListItemIcon>
                <ListItemText primary={t('common:login', 'Login')} />
              </MenuItem>
              <MenuItem onClick={handleRegister}>
                <ListItemIcon>
                  <PersonIcon sx={{ color: '#d4af37' }} />
                </ListItemIcon>
                <ListItemText primary={t('common:register', 'Register')} />
              </MenuItem>
            </Menu>
          </>
        )}
        
      {/* Info Icon */}
        <IconButton 
          component={RouterLink} 
          to="/about" 
          aria-label="About" 
          sx={{ 
            color: 'rgba(255, 255, 255, 0.6)',
            '&:hover': { 
              color: 'rgba(255, 255, 255, 0.9)',
              backgroundColor: 'rgba(255, 255, 255, 0.1)' 
            }
          }}
        >
          <InfoIcon />
        </IconButton>
      </Box>

      {/* Medieval-themed decorative elements */}
      <Box sx={{ 
        position: 'absolute', 
        top: 0, 
        left: 0, 
        right: 0, 
        height: '100%', 
        opacity: 0.1,
        backgroundImage: `
          radial-gradient(circle at 20% 30%, #d4af37 0.5%, transparent 1.5%),
          radial-gradient(circle at 80% 40%, #d4af37 0.5%, transparent 1.5%),
          radial-gradient(circle at 40% 80%, #d4af37 0.5%, transparent 1.5%),
          radial-gradient(circle at 60% 10%, #d4af37 0.5%, transparent 1.5%)
        `,
        pointerEvents: 'none'
      }} />

      {/* Main Content */}
      <Container 
        maxWidth="sm" 
        sx={{ 
          flexGrow: 1, 
          display: 'flex', 
          flexDirection: 'column', 
          justifyContent: 'center', 
          alignItems: 'center',
          color: 'white',
          pt: { xs: 10, sm: 2 },
          pb: { xs: 6, sm: 2 }
        }}
      >
        {/* Logo */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'center' }}>
          <img 
            src={logoImage} 
            alt="Gauntlet Debates Logo" 
            style={{ 
              height: '80px', 
              width: 'auto',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
            }} 
          />
        </Box>

        <Typography
          component="h1"
          variant="h2"
          align="center"
          color="inherit"
          sx={{ 
            mb: 6, 
            fontWeight: 'bold',
            textShadow: '0 2px 4px rgba(0,0,0,0.5)',
            letterSpacing: '0.05em',
            whiteSpace: { xs: 'normal', sm: 'nowrap' },
            fontSize: { xs: '1.75rem', sm: '2.125rem' },
            px: { xs: 2, sm: 0 }
          }}
        >
          {(() => {
            const key = 'landingPage.title';
            const result = t(key, 'Gauntlet Debates™');
            return result;
          })()}
        </Typography>

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            flexDirection: isMobile ? 'column' : 'row',
            gap: 2,
            width: '100%',
            mb: 4
          }}
        >
          {isAuthenticated ? (
            <>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => navigate('/home')}
                sx={{ 
                  backgroundColor: '#d4af37', 
                  color: '#1a1a2e',
                  '&:hover': { backgroundColor: '#c4a027' }
                }}
                fullWidth={isMobile}
              >
                {t('yourDebates', 'Your Debates')}
              </Button>
              <EmailVerificationGuard action="create a debate">
                <Button 
                  variant="outlined" 
                  size="large"
                  onClick={() => navigate('/debates/new')}
                  sx={{ 
                    borderColor: '#d4af37', 
                    color: '#d4af37',
                    '&:hover': { borderColor: '#c4a027', color: '#c4a027' }
                  }}
                  fullWidth={isMobile}
                >
                  {t('createDebate', 'Create Debate')}
                </Button>
              </EmailVerificationGuard>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => navigate('/debates/public')}
                sx={{ 
                  borderColor: '#d4af37', 
                  color: '#d4af37',
                  '&:hover': { borderColor: '#c4a027', color: '#c4a027' }
                }}
                fullWidth={isMobile}
              >
                {t('publicDebates', 'Public Debates')}
              </Button>
            </>
          ) : (
            <>
              <Button 
                variant="contained" 
                size="large"
                onClick={() => navigate('/login')}
                sx={{ 
                  backgroundColor: '#d4af37', 
                  color: '#1a1a2e',
                  '&:hover': { backgroundColor: '#c4a027' }
                }}
                fullWidth={isMobile}
              >
                {(() => {
                  const result = t('login', 'Login');
                                      logger.info(`🔍 LOGIN BUTTON: t('login') = "${result}" (lang: ${i18n.language})`);
                  return result;
                })()}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => navigate('/register')}
                sx={{ 
                  borderColor: '#d4af37', 
                  color: '#d4af37',
                  '&:hover': { borderColor: '#c4a027', color: '#c4a027' }
                }}
                fullWidth={isMobile}
              >
                {t('register', 'Register')}
              </Button>
              <Button 
                variant="outlined" 
                size="large"
                onClick={() => navigate('/debates/public')}
                sx={{ 
                  borderColor: '#d4af37', 
                  color: '#d4af37',
                  '&:hover': { borderColor: '#c4a027', color: '#c4a027' }
                }}
                fullWidth={isMobile}
              >
                {t('publicDebates', 'Public Debates')}
              </Button>
            </>
          )}
        </Box>

        {/* Feature Cards */}
        <Grid container spacing={2} sx={{ mt: 2 }}>
          {/* CUBE */}
          <Grid item xs={12} sm={6}>
            <Card 
              sx={{ 
                backgroundColor: 'rgba(26, 26, 46, 0.7)', 
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardActionArea 
                component={RouterLink} 
                to="/cube"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  p: 2 
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ mr: 1, color: '#d4af37' }}>
                    <CubeIcon />
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ color: '#fff' }}>
                    {(() => {
                      const key = 'landingPage.cubeTitle';
                      const result = t(key, 'CUBE');
                      return result;
                    })()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {t('landingPage.cubeDescription', 'Discover the Current Best Explanations on any topic from our highest quality debates.')}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
          
          {/* Leaderboards */}
          <Grid item xs={12} sm={6}>
            <Card 
              sx={{ 
                backgroundColor: 'rgba(26, 26, 46, 0.7)', 
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(212, 175, 55, 0.3)',
                height: '100%',
                transition: 'transform 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 6px 12px rgba(0,0,0,0.2)'
                }
              }}
            >
              <CardActionArea 
                component={RouterLink} 
                to="/leaderboards"
                sx={{ 
                  height: '100%', 
                  display: 'flex', 
                  flexDirection: 'column', 
                  alignItems: 'flex-start',
                  justifyContent: 'flex-start',
                  p: 2 
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Box sx={{ mr: 1, color: '#d4af37' }}>
                    <TrophyIcon />
                  </Box>
                  <Typography variant="h6" component="h2" sx={{ color: '#fff' }}>
                    {(() => {
                      const key = 'landingPage.leaderboardsTitle';
                      const result = t(key, 'Leaderboards');
                      return result;
                    })()}
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                  {t('landingPage.leaderboardsDescription', 'See how you rank among debaters and track your progress on the Excalibur Index.')}
                </Typography>
              </CardActionArea>
            </Card>
          </Grid>
        </Grid>
      </Container>
    </Box>
  );
};

export default LandingPage; 